<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';

import { onLongPress } from '@vueuse/core';

import dotsIcon from '@/assets/icons/dots.svg';
import eyeIcon from '@/assets/icons/eye.svg';
import { isTouchDevice } from '@/helpers/device';
import { isOnAttr } from '@/helpers/events';
import { formatTokenAmount } from '@/helpers/formatters';
import { AmountMode } from '@/references/axios/transactions/types';
import { getNetwork, type Network } from '@/references/network';
import type { Token } from '@/references/tokens';

import TokenImage from '@/components/TokenImage.vue';
import UiButtonIcon from '@/components/ui/UiButtonIcon.vue';
import type { UiDropdownItem } from '@/components/ui/UiDropdown.types';
import UiDropdown from '@/components/ui/UiDropdown.vue';
import UiText from '@/components/ui/UiText.vue';

defineOptions({
  inheritAttrs: false
});

interface Props {
  token: Token;
  description: string;
  value: string;
  volatility?: number;
  networks?: Network[];
  readonly?: boolean;
  selected?: boolean;
  disabled?: boolean;
  context?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  networks: undefined,
  readonly: false,
  selected: false,
  disabled: false,
  volatility: undefined,
  context: false
});

const dropdownComponent = ref<typeof UiDropdown>();
const buttonElement = ref<HTMLElement>();

const isTouchable = isTouchDevice();

const attrs = useAttrs();
// const { push } = useRouter();
const { t } = useI18n();
// const { setLastOpenedToken } = useTokenDetails();

const classList = computed(
  (): Record<string, boolean> => ({
    'token-info_disabled': props.disabled,
    'token-info_selected': props.selected,
    'token-info_with-dots': !isTouchable && props.context && !props.disabled && !props.selected,
    'token-info_touchable': isTouchable && props.context && !props.disabled && !props.selected,
    'token-info_with-opened-dropdown':
      !!dropdownComponent.value?.isOpened && !props.disabled && !props.selected
  })
);

const listenersAttrs = computed(
  (): Record<string, unknown> =>
    Object.fromEntries(Object.entries(attrs).filter(([key]) => isOnAttr(key)))
);

const notListenersAttrs = computed(
  (): Record<string, unknown> =>
    Object.fromEntries(Object.entries(attrs).filter(([key]) => !isOnAttr(key)))
);

const isPositive = computed(
  (): boolean => typeof props.volatility === 'number' && props.volatility >= 0
);

const formattedVolatility = computed(() =>
  typeof props.volatility === 'number'
    ? `${formatTokenAmount(props.volatility, 2, '', AmountMode.ValueOnly, false, 2)}%`
    : ''
);

const options = computed((): UiDropdownItem[] => [
  {
    text: t('phrases.viewChart'),
    icon: eyeIcon,
    handler() {
      // setLastOpenedToken(props.token);
      // push({
      //   name: RouteNames.Token,
      //   params: { address: props.token.address, network: props.token.network }
      // });
    }
  }
]);

function onContextMenu(event: Event): void {
  if (!isTouchable || !props.context || props.disabled || props.selected) {
    return;
  }

  event.preventDefault();
}

function onLongPressCallbackHook(): void {
  if (!isTouchable || !props.context || props.disabled || props.selected) {
    return;
  }

  // Trigger click outside in others dropdowns.
  document.body.click();

  dropdownComponent.value?.open();
}

onLongPress(buttonElement, onLongPressCallbackHook, { modifiers: { prevent: true } });
</script>

<template>
  <span class="token-info" :class="classList" v-bind="notListenersAttrs">
    <component
      v-bind="listenersAttrs"
      :is="props.readonly ? 'div' : 'button'"
      ref="buttonElement"
      class="token-info__button"
      :type="props.readonly ? undefined : 'button'"
      @contextmenu="onContextMenu"
    >
      <TokenImage
        class="token-info__image"
        :token="props.token"
        :hide-network-icon="props.networks && props.networks.length > 1"
      />
      <span class="token-info__left">
        <UiText class="token-info__title" size="md" weight="medium">
          {{ props.token.name }}
        </UiText>
        <span class="token-info__footer">
          <ul v-if="props.networks && props.networks.length > 1" class="token-info__network-list">
            <li v-for="network in props.networks" :key="network" class="token-info__network-item">
              <img
                class="token-info__network-image"
                :src="getNetwork(network)?.iconURL"
                :alt="getNetwork(network)?.displayedName"
                :title="getNetwork(network)?.displayedName"
              />
            </li>
          </ul>
          <UiText class="token-info__description" size="sm" weight="medium" muted>
            {{ props.description }}
          </UiText>
        </span>
      </span>
      <span class="token-info__right">
        <UiText class="token-info__value" weight="medium" size="sm">
          {{ props.value }}
        </UiText>
        <UiText
          v-if="formattedVolatility"
          class="token-info__volatility"
          :class="{ 'token-info__volatility_positive': isPositive }"
          size="sm"
        >
          {{ formattedVolatility }}
        </UiText>
      </span>
    </component>
    <UiDropdown
      v-if="props.context && !props.disabled && !props.selected"
      ref="dropdownComponent"
      class="token-info__dropdown"
      :position="isTouchable ? 'bottom-start' : 'bottom-end'"
      :margin="isTouchable ? 0 : 8"
      :distance="isTouchable ? 16 : 4"
      :items="options"
    >
      <UiButtonIcon
        v-if="!isTouchable"
        class="token-info__dropdown-button"
        :icon="dotsIcon"
        mod="transparent"
      />
    </UiDropdown>
  </span>
</template>

<style scoped>
.token-info {
  position: relative;
  display: block;
  width: 100%;
  transition: opacity var(--animation-micro) var(--animation-effect);
}

.token-info_disabled {
  opacity: 0.25;
}

.token-info__button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 12px 0;
  transition: opacity var(--animation-micro) var(--animation-effect);
}

button.token-info__button {
  margin: 0;
  color: inherit;
  font-weight: inherit;
  font-size: inherit;
  font-family: inherit;
  line-height: inherit;
  text-align: left;
  background-color: transparent;
  border: none;
  border-radius: 0;
  cursor: pointer;
  appearance: none;
  user-select: none;
}

.token-info_touchable.token-info_with-opened-dropdown .token-info__button {
  opacity: 0.25;
}

.token-info_selected .token-info__button {
  cursor: wait;
}

.token-info_disabled .token-info__button {
  cursor: default;
}

.token-info__image {
  flex-shrink: 0;
  margin: 0 16px 0 0;
}

.token-info__left {
  margin: 0 16px 0 0;
}

.token-info__title {
  display: block;
  word-break: break-word;
}

.token-info__footer {
  display: flex;
  align-items: center;
}

.token-info__network-list {
  display: flex;
  align-items: center;
  margin: 0 8px 0 0;
  padding: 0;
  list-style-type: none;
}

.token-info__network-item:not(:first-child) {
  margin: 0 0 0 -8px;
}

.token-info__network-image {
  display: block;
  width: 20px;
  height: 20px;
  margin: -2px;
  border: 2px solid var(--color-white);
  border-radius: 50%;
}

.token-info__description {
  display: block;
  text-transform: uppercase;
  word-break: break-word;
}

.token-info__right {
  margin: 0 0 0 auto;
  text-align: right;
  transition: opacity var(--animation-micro) var(--animation-effect);
}

.token-info_with-dots:hover .token-info__right,
.token-info_with-opened-dropdown:not(.token-info_touchable) .token-info__right {
  opacity: 0;
}

.token-info__value {
  display: block;
}

.token-info__value:not(:last-child) {
  margin: 0 0 2px;
}

.token-info__volatility {
  display: block;
  color: var(--color-secondary-text);
}

.token-info__volatility_positive {
  color: var(--color-green);
}

.token-info__dropdown {
  position: absolute;
}

.token-info_with-dots .token-info__dropdown {
  top: calc(50% - 20px);
  right: -8px;
}

.token-info_touchable .token-info__dropdown {
  top: calc(50% + 22px);
  left: 0;
}

.token-info__dropdown-button {
  opacity: 0;
  transition: opacity var(--animation-micro) var(--animation-effect);
}

.token-info_with-dots:hover .token-info__dropdown-button,
.token-info_with-opened-dropdown .token-info__dropdown-button {
  opacity: 1;
}
</style>
